import React, { useState, useEffect, useMemo } from 'react';
import { subscribeToUserCollection } from '../utils/userDataHelper';
import { Link } from 'react-router-dom';
import { num } from './utils/costCalculations';
import '../styles/dashboard.css';
import { useAuth } from '../contexts/AuthContext';
import Breadcrumbs from '../Components/Breadcrumbs';
import Footer from '../Components/Footer';
import { formatCurrency } from '../utils/currency';
import useCurrencyTools from '../hooks/useCurrencyTools';
import { inventoryStock, inventoryUnit, convertQuantity, inventoryDensity, getBatchReadyDate } from '../utils/workflowPlanning';

function LabDashboard() {
  const [ingredients, setIngredients] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [batches, setBatches] = useState([]);
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const { currencyCode } = useAuth();
  const currency = currencyCode || 'INR';
  const { convertMoney } = useCurrencyTools();
  const formatMoney = (value, fractionDigits = 0) =>
    formatCurrency(num(value, 0), currency, {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    });

  useEffect(() => {
    const unsubs = [];
    try {
      unsubs.push(subscribeToUserCollection('ingredients', setIngredients));
      unsubs.push(subscribeToUserCollection('recipes', setRecipes));
      unsubs.push(subscribeToUserCollection('batches', (data) => { setBatches(data); setLoading(false); }));
      unsubs.push(subscribeToUserCollection('sales', setSales));
    } catch (error) {
      console.error('Error loading data:', error);
      setLoading(false);
    }
    return () => unsubs.forEach(u => u());
  }, []);

  const inventoryAlerts = useMemo(() => {
    return ingredients.map(ing => {
      let stock;
      let minimum;
      try {
        stock = convertQuantity(inventoryStock(ing), inventoryUnit(ing), 'g', inventoryDensity(ing));
        minimum = ing.minStockGrams !== undefined ? Number(ing.minStockGrams) : ing.minStock !== undefined ? convertQuantity(ing.minStock, inventoryUnit(ing), 'g', inventoryDensity(ing)) : 100;
      } catch { return null; }
      return { ...ing, currentStock: stock, minStock: minimum };
    }).filter(ing => ing && ing.currentStock < ing.minStock);
  }, [ingredients]);

  const batchAlerts = useMemo(() => {
    const today = new Date();
    const alerts = [];
    batches.forEach(batch => {
      if (batch.status === 'curing' && batch.madeDate) {
        const readyDate = getBatchReadyDate(batch, recipes.find(recipe => recipe.id === batch.recipeId));
        if (!readyDate) return;
        const daysLeft = Math.ceil((readyDate - today) / (1000 * 60 * 60 * 24));
        if (daysLeft <= 0) {
          alerts.push({ ...batch, alertType: 'ready', message: 'Ready for testing!' });
        } else if (daysLeft <= 7) {
          alerts.push({ ...batch, alertType: 'soon', message: `Ready in ${daysLeft} days` });
        }
      }
      if (batch.soapExpiryDate) {
        const expDate = new Date(batch.soapExpiryDate);
        const daysToExpiry = Math.ceil((expDate - today) / (1000 * 60 * 60 * 24));
        if (daysToExpiry <= 30 && daysToExpiry > 0) {
          alerts.push({ ...batch, alertType: 'expiring', message: `Expires in ${daysToExpiry} days` });
        } else if (daysToExpiry <= 0) {
          alerts.push({ ...batch, alertType: 'expired', message: 'Expired!' });
        }
      }
    });
    return alerts;
  }, [batches, recipes]);

  const salesMetrics = useMemo(() => {
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const thisMonthSales = sales.filter(s => new Date(s.date) >= monthStart);
    return {
      totalRevenue: thisMonthSales.reduce((sum, s) => sum + convertMoney(num(s.totalAmount, 0), s.currencyCode || 'INR', currency), 0),
      totalProfit: thisMonthSales.reduce((sum, s) => sum + convertMoney(num(s.profit, 0), s.currencyCode || 'INR', currency), 0),
      totalOrders: thisMonthSales.length,
      unitsSold: thisMonthSales.reduce((sum, s) => sum + num(s.quantity, 0), 0),
    };
  }, [sales, convertMoney, currency]);

  const stats = {
    recipes: recipes.length,
    ingredients: ingredients.length,
    curingBatches: batches.filter(b => b.status === 'curing').length,
    readyBatches: batches.filter(b => b.status === 'ready').length,
  };

  const header = (
    <div className="pageheader borderleft">
      <Breadcrumbs items={[{ label: 'Home', to: '/' }, { label: 'Dashboard' }]} />
      <div className="pageheader-row">
        <div className="pageheading">Dashboard</div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="sl-page">
        {header}
        <div className="borderleft ld-loading">Loading your workshop…</div>
      </div>
    );
  }

  const alertCount = inventoryAlerts.length + batchAlerts.length;
  const workshop = [
    { to: '/recipes', value: stats.recipes, label: 'Recipes' },
    { to: '/inventory', value: stats.ingredients, label: 'Ingredients' },
    { to: '/batches', value: stats.curingBatches, label: 'Batches curing' },
    { to: '/batches', value: stats.readyBatches, label: 'Batches ready to sell' },
  ];
  const month = [
    { value: formatMoney(salesMetrics.totalRevenue, 0), label: 'Revenue' },
    { value: formatMoney(salesMetrics.totalProfit, 0), label: 'Profit' },
    { value: salesMetrics.totalOrders, label: 'Orders' },
    { value: salesMetrics.unitsSold, label: 'Units sold' },
  ];
  const actions = [
    { to: '/calculator', name: 'Create a recipe', desc: 'Build a new soap formula in the lye calculator' },
    { to: '/generator', name: 'Recipe generator', desc: 'Starting-point oil blends to test' },
    { to: '/quickbatch', name: 'Quick batch', desc: 'Start production from a saved recipe' },
    { to: '/recipes', name: 'My recipes', desc: `${stats.recipes} saved` },
    { to: '/batches', name: 'Batches', desc: `${stats.curingBatches} curing now` },
    { to: '/inventory', name: 'Inventory', desc: inventoryAlerts.length > 0 ? `${inventoryAlerts.length} running low` : 'Everything is stocked' },
    { to: '/sales', name: 'Sales', desc: 'Record sales and see revenue' },
    { to: '/print', name: 'Print centre', desc: 'Recipe sheets and labels' },
  ];

  return (
    <div className="sl-page">
      {header}

      <div className="borderleft">
        {alertCount > 0 && (
          <section className="sl-band">
            <h2 className="sl-subtitle">Needs attention <span className="ld-count">{alertCount}</span></h2>
            <ul className="ld-alerts">
              {inventoryAlerts.map(ing => (
                <li key={ing.id} className="ld-alert ld-alert--warning">
                  <div>
                    <strong>Low stock: {ing.name}</strong>
                    <span>{ing.currentStock}g left (minimum {ing.minStock}g)</span>
                  </div>
                  <Link to="/inventory" className="sl-arrow-link">Restock</Link>
                </li>
              ))}
              {batchAlerts.map((batch, i) => (
                <li key={`${batch.id}-${i}`} className={`ld-alert ${batch.alertType === 'ready' ? 'ld-alert--success' : batch.alertType === 'expired' ? 'ld-alert--error' : 'ld-alert--warning'}`}>
                  <div>
                    <strong>{batch.name || 'Batch'}</strong>
                    <span>{batch.message}</span>
                  </div>
                  <Link to="/batches" className="sl-arrow-link">View</Link>
                </li>
              ))}
            </ul>
          </section>
        )}

        <section className="sl-band">
          <h2 className="sl-subtitle">Your workshop</h2>
          <div className="sl-rule-grid cols-4 sl-figure-grid">
            {workshop.map(item => (
              <Link key={item.label} to={item.to} className="sl-rule-cell">
                <span className="sl-figure">{item.value}</span>
                <span className="sl-figure-label">{item.label}</span>
              </Link>
            ))}
          </div>
        </section>

        <section className="sl-band">
          <h2 className="sl-subtitle">This month</h2>
          <div className="sl-rule-grid cols-4 sl-figure-grid">
            {month.map(item => (
              <div key={item.label}>
                <span className="sl-figure">{item.value}</span>
                <span className="sl-figure-label">{item.label}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="sl-band">
          <h2 className="sl-subtitle">Quick actions</h2>
          <div className="sl-rule-grid cols-4">
            {actions.map(action => (
              <Link key={action.to + action.name} to={action.to} className="sl-rule-cell">
                <h3>{action.name}</h3>
                <p>{action.desc}</p>
              </Link>
            ))}
          </div>
        </section>
      </div>
      <Footer />
    </div>
  );
}

export default LabDashboard;
